import invariant from 'invariant';

import { RuleGroupNames } from '@candu-ai/segments/types';
import { generateUniqueId } from 'canoe/utils';
import { emptySingleRuleGroup } from 'dashboard/pages/Segment/components/constants';
import {
  SegmentBaseDefinition,
  SegmentNestedDefinition,
  SegmentSingleDefinition,
} from 'dashboard/pages/Segment/components/types';

export type DefinitionItem =
  | SegmentBaseDefinition
  | SegmentNestedDefinition
  | SegmentSingleDefinition;

export const isSingleRuleGroup = <Item extends DefinitionItem>(ruleGroup: Item): boolean =>
  ruleGroup.object === RuleGroupNames.Single;

export const addRuleToRuleGroup = <Item extends DefinitionItem>(ruleGroup: Item): Item => {
  const newGroup = { ...emptySingleRuleGroup, groupId: generateUniqueId() };

  if (isSingleRuleGroup(ruleGroup)) {
    return {
      object: RuleGroupNames.All,
      groups: [ruleGroup, newGroup],
    } as Item;
  }
  return {
    ...ruleGroup,
    groups: [...(ruleGroup as SegmentBaseDefinition | SegmentNestedDefinition).groups, newGroup],
  } as Item;
};

export const removeRuleFromRuleGroup = <Item extends DefinitionItem>(
  ruleGroup: Item,
  indexToRemove: number,
): Item => {
  // sanity check...
  invariant(
    !isSingleRuleGroup(ruleGroup),
    '(RuleGroup) Refusing to remove a rule from a rule group that only has one rule',
  );
  const { groups } = ruleGroup;
  invariant(
    indexToRemove >= 0 && groups && indexToRemove < groups.length,
    '(RuleGroup) You are trying to remove an index that is too large or too small',
  );

  const groupsWithoutElement = groups?.filter((value, index) => indexToRemove !== index);

  if (groupsWithoutElement.length > 1) {
    return { ...ruleGroup, groups: groupsWithoutElement } as Item;
  }
  return {
    // @ts-expect-error
    object: RuleGroupNames.Single,
    ...groupsWithoutElement[0],
  } as Item;
};
